/* Iter 102 (2026-02) — Menu IA v3 · Stage 2A · Batch A Exposure.
   ─────────────────────────────────────────────────────────────────
   Executes Stage 2A per NAVIGATION_STAGE_2A_IMPACT_SUMMARY.md — a
   focused batch of 20 previously-orphan URLs exposed as L3 leaves
   into their operator-approved L2 subgroups. Proposal Templates
   (FPR-001) intentionally deferred pending Proposal Builder BA.

   Stage 2A additions (per Feature Placement Register — 20 leaves):
     Sales → Delivery                +1  (FPR-002 Orphan AMC Queue)
     Accounts → Reports              +3  (FPR-003/004/005)
     Vendors → Reports NEW L2        +3  (FPR-006/007/008)
     Workforce → Attendance          +3  (FPR-019/020/021)
     Workforce → Attendance Reports  +5  (FPR-024–028)
     Payroll → Compliance            +3  (FPR-038/039/040)
     Platform Setup → Templates      +1  (FPR-047 HR Templates)
     Platform Setup → Diagnostics    +1  (FPR-048 Email Diagnostic)

   Stage 2A invariants (all held — ADR-NAV-001):
     - URLs / permissions / backend / App.js: UNCHANGED (0)
     - New L2 subgroups: 1 (Vendors → Reports)
     - New L3 leaves: 20
     - L2 ceiling ≤ 7 per L1 (Vendors 2→3; Workforce stays at 7)
     - L3 ceiling ≤ 12 per L2 (max after 2A: Accounts→Reports = 8)
     - Root-item cap ≤ 1 per L1 (only Employees→Directory)
     - All permission gates inherited from L2 siblings

   Prior iteration lineage (still active):
     Iter 89  — Menu IA v2 Gate B (label locks: Data Hub, Vendor Master,
                Desktop Presence, Platform Setup split from Admin).
     Iter 100 — Menu IA v3 Stage 1 (Dashboard promoted to root,
                Workforce/Employees swap, 7 label renames).
     Iter 101 — Stage 1.5 True L2 Accordion (L1 single-open,
                L2 multi-open, per-user localStorage persistence).
     Iter 102 — Stage 2A · this iteration.

   Guard tests: /app/backend/tests/test_menu_ia_v2_guard.py — 24 tests
   (17 architecture + 7 Stage 2A cluster assertions).

   Behaviour (unchanged from Iter 101):
     1. L1 accordion — single-open, auto-expand on deep-link.
     2. L2 accordion — multi-open, independently toggled, persisted.
     3. Per-user localStorage persistence (L1 + L2 keys).
     4. Collapsed (icon-rail) mode with hover flyouts.
     5. Role-based visibility — items + subgroups + groups auto-hide
        if all descendants fail their permission gates.
   */

import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
    CaretRight, CaretLeft,
    Sidebar as SidebarIcon,
} from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";
import { cn } from "../../lib/utils";
import {
    NAV_GROUPS,
    DASHBOARD_ITEM,
    isItemVisible,
    resolveGroupDisplay,
    findOwnersForPath,
    findGroupForPath,
} from "../../lib/navRegistry";

// ── Visibility helpers, NAV_GROUPS and DASHBOARD_ITEM live in
//    `lib/navRegistry.js` since Iter 103.0 Phase 1. Sidebar.jsx
//    re-imports them so the same registry can be consumed by
//    GlobalSearchBar (Iter 103.0 Phase 2). No behavioural change
//    in Phase 1 — this is a pure lift-and-shift.

// ── Storage keys ───────────────────────────────────────────────────
// Per-user accordion state: `reedos.sidebar.expandedGroup.{email}`
// holds the currently-open L1 module id (single-open).
// `reedos.sidebar.expandedSubgroups.{email}` holds the L2 open-set
// (multi-open) as a JSON-serialised string array.
// Global collapse state: `reedos.sidebar.collapsed`. Not user-scoped
// because it's a screen-size preference — most operators use the same
// machine and expect the same chrome state across accounts.
const STORAGE_EXPANDED_KEY = (email) => `reedos.sidebar.expandedGroup.${email || "anon"}`;
const STORAGE_EXPANDED_L2_KEY = (email) => `reedos.sidebar.expandedSubgroups.${email || "anon"}`;
const STORAGE_COLLAPSED_KEY = "reedos.sidebar.collapsed";

// ── Components ─────────────────────────────────────────────────────

function NavItemRow({ item, collapsed, accent }) {
    const Icon = item.icon;
    const location = useLocation();
    const active = location.pathname.startsWith(item.to);
    const testId = `nav-${item.label.toLowerCase().replace(/[\s&]+/g, "-")}`;
    // PROP-001 / ADR-001 — accent fallback to slate keeps unaccented
    // groups (sub-groups without an explicit override AND a parent without
    // one) rendering exactly as today. Each top-level group in NAV_GROUPS
    // carries an accent, so in practice `a` is always a real palette.
    const a = accent || "slate";
    if (collapsed) {
        return (
            <NavLink
                to={item.to}
                data-testid={testId}
                title={item.label}
                className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                    active
                        ? `bg-${a}-50 text-${a}-900 font-medium`
                        : `text-slate-600 hover:bg-${a}-50 hover:text-${a}-800`,
                )}
            >
                <Icon
                    size={16}
                    weight={active ? "fill" : "regular"}
                    className={active ? `text-${a}-700` : ""}
                />
                <span className="truncate">{item.label}</span>
            </NavLink>
        );
    }
    // Iter 102.3 · Visual Hierarchy — Bold Option C.
    // L3 leaves now render INSIDE an L2 container band. To make each L3
    // "obviously belong" to its parent L2, we:
    //   • add slight left indent (pl-3) so the row visually sits inside
    //     the L2 compartment
    //   • keep the accent left-border for active-state (identity signal)
    //   • use accent-tinted background on active for stronger feedback
    //   • bump hover to accent-50 for a clearer affordance
    return (
        <NavLink
            to={item.to}
            data-testid={testId}
            title={item.label}
            className={cn(
                "flex items-center gap-2.5 pl-3 pr-2.5 py-1.5 rounded-md text-[13px] transition-colors border-l-2",
                active
                    ? `bg-${a}-50 text-${a}-900 font-semibold border-${a}-600`
                    : `text-slate-600 hover:bg-${a}-50/70 hover:text-${a}-800 border-transparent`,
            )}
        >
            <Icon
                size={14}
                weight={active ? "fill" : "regular"}
                className={cn("shrink-0", active ? `text-${a}-700` : "text-slate-400")}
            />
            <span className="truncate">{item.label}</span>
        </NavLink>
    );
}

/** Iter 101 · Stage 1.5 — Renders a single L2 subgroup as an
 *  independently expandable accordion. The header button toggles
 *  the body via the multi-open `expandedSubgroups` set held on
 *  the parent Sidebar component. Body is not mounted when
 *  collapsed (progressive disclosure). Fully accessible via
 *  aria-expanded + aria-controls. */
function SubgroupAccordion({ subgroup, isExpanded, onToggle, accent, collapsed = false }) {
    const SgIcon = subgroup.icon;
    const sgTestId = `nav-subgroup-${subgroup.id}`;
    const a = accent || "slate";
    // Iter 102.3 · Visual Hierarchy — Bold Option C.
    // When EXPANDED, the L2 subgroup renders as a "compartment" — a soft
    // container band with subtle background tint + border + rounded corners
    // — so the child L3 leaves visually belong to their parent L2 at a
    // glance, without reading the labels. The L2 header itself becomes
    // more prominent (larger eyebrow text, stronger color, larger caret).
    return (
        <div
            className={cn(
                "transition-all",
                isExpanded ? "mt-2 mb-1" : "mt-1",
            )}
            data-testid={sgTestId}
        >
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={isExpanded}
                aria-controls={`${sgTestId}-body`}
                data-testid={`${sgTestId}-toggle`}
                className={cn(
                    "w-full flex items-center justify-between pl-2 pr-2 py-1.5 rounded-md transition-colors group/sg",
                    isExpanded
                        ? "bg-slate-100"
                        : "hover:bg-slate-100/70",
                )}
            >
                <span
                    className={cn(
                        "reedos-eyebrow text-[10px] tracking-[0.16em] uppercase font-semibold flex items-center gap-1.5 transition-colors",
                        isExpanded
                            ? "text-slate-800"
                            : "text-slate-500 group-hover/sg:text-slate-800",
                    )}
                >
                    <span
                        className={cn(
                            "inline-block h-3 w-[3px] rounded-full transition-colors",
                            isExpanded ? `bg-${a}-500` : `bg-${a}-300`,
                        )}
                    />
                    <SgIcon
                        size={12}
                        weight={isExpanded ? "fill" : "regular"}
                        className={isExpanded ? `text-${a}-700` : `text-${a}-500`}
                    />
                    {subgroup.label}
                </span>
                <CaretRight
                    size={11}
                    weight="bold"
                    className={cn(
                        "shrink-0 transition-transform",
                        isExpanded ? `rotate-90 text-${a}-600` : "text-slate-400 group-hover/sg:text-slate-600",
                    )}
                />
            </button>
            {isExpanded && (
                <div
                    id={`${sgTestId}-body`}
                    data-testid={`${sgTestId}-body`}
                    className={cn(
                        // The compartment band — clearly-visible slate-tinted
                        // background + border + rounded corners + accent
                        // left-rail. This is the visual "container" that
                        // makes L3 items obviously belong to their parent L2
                        // subgroup at a glance, without reading the labels.
                        "mt-1.5 ml-1 mr-0.5 pl-2 pr-1 pt-2 pb-2 space-y-0.5 rounded-md bg-slate-50 border border-slate-200 border-l-[3px]",
                        `border-l-${a}-400`,
                    )}
                >
                    {subgroup.items.map((it) => (
                        <NavItemRow key={it.to} item={it} collapsed={collapsed} accent={a} />
                    ))}
                </div>
            )}
        </div>
    );
}

/** Renders a single L1 group's expanded content — root items
 *  (module-level `items[]`) followed by L2 subgroups rendered as
 *  independently expandable accordions (Iter 101 · Stage 1.5).
 *  Used both in the in-place expanded L1 accordion and in the
 *  collapsed-mode hover flyout.
 *
 *  PROP-001 / ADR-001 — `accent` is the parent group's module accent;
 *  each visibleSubgroup may carry its own `accent` override (e.g.
 *  Loans=emerald inside Payroll=pink). Items inside a sub-group take
 *  the sub-group's accent (or the parent's if none). */
function GroupBody({ group, visibleItems, visibleSubgroups, collapsed = false, accent, expandedSubgroups, onToggleSubgroup }) {
    const a = accent || group.accent;
    return (
        <div className="space-y-0.5" data-testid={`nav-group-body-${group.id}`}>
            {visibleItems.map((it) => (
                <NavItemRow key={it.to} item={it} collapsed={collapsed} accent={a} />
            ))}
            {visibleSubgroups.map((sg) => {
                const sgAccent = sg.accent || a;
                const isExpanded = expandedSubgroups.has(sg.id);
                return (
                    <SubgroupAccordion
                        key={sg.id}
                        subgroup={sg}
                        isExpanded={isExpanded}
                        onToggle={() => onToggleSubgroup(sg.id)}
                        accent={sgAccent}
                        collapsed={collapsed}
                    />
                );
            })}
        </div>
    );
}

function GroupAccordion({ group, expanded, onToggle, currentPath, user, can, expandedSubgroups, onToggleSubgroup }) {
    const Icon = group.icon;
    // Visibility filter per render — keeps the rule co-located with
    // the gate definitions on each item.
    const visibleItems = (group.items || []).filter((i) => isItemVisible(i, user, can));
    const visibleSubgroups = (group.subgroups || [])
        .map((sg) => ({ ...sg, items: sg.items.filter((i) => isItemVisible(i, user, can)) }))
        .filter((sg) => sg.items.length > 0);
    if (visibleItems.length === 0 && visibleSubgroups.length === 0) {
        return null;
    }
    // UAT refinement: re-label / re-shape the group for non-admin
    // and manager personas (see resolveGroupDisplay above).
    const displayed = resolveGroupDisplay(group, visibleItems, visibleSubgroups, can);
    const groupOwnsCurrent = findGroupForPath(currentPath) === group.id;
    const groupTestId = `nav-group-${group.id}`;
    // PROP-001 / ADR-001 — accent fallback chain: group.accent → slate.
    const a = group.accent || "slate";
    // Iter 102.3 · Visual Hierarchy — Bold Option C.
    // L1 module header gets stronger visual presence: larger uppercase
    // eyebrow (12px), semibold weight, larger icon, more prominent active
    // accent bar. Modules also get slightly more vertical breathing room
    // via the wrapper padding so each L1 block reads as a distinct unit.
    return (
        <div className="px-2 py-0.5" data-testid={groupTestId}>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                data-testid={`${groupTestId}-toggle`}
                className={cn(
                    "w-full flex items-center justify-between gap-2 pl-2.5 pr-3 py-2 rounded-md text-sm transition-colors border-l-[3px]",
                    groupOwnsCurrent
                        ? `text-slate-900 font-bold border-${a}-600 bg-${a}-50/40`
                        : expanded
                            ? `text-slate-800 font-semibold border-${a}-300 bg-slate-50/60`
                            : `text-slate-500 hover:text-slate-900 hover:bg-${a}-50/50 border-transparent`,
                )}
            >
                <span className="flex items-center gap-2.5 min-w-0">
                    <Icon
                        size={18}
                        weight={groupOwnsCurrent ? "fill" : "regular"}
                        className={groupOwnsCurrent ? `text-${a}-700` : `text-${a}-600`}
                    />
                    <span className="uppercase tracking-[0.16em] text-[12px] font-display font-semibold">{displayed.label}</span>
                </span>
                <CaretRight
                    size={13}
                    weight="bold"
                    className={cn(
                        "shrink-0 transition-transform duration-200",
                        groupOwnsCurrent ? `text-${a}-600` : "text-slate-400",
                        expanded && "rotate-90",
                    )}
                />
            </button>
            {expanded && (
                <div className="pl-2 pr-0.5 pt-0.5 pb-1.5" data-testid={`${groupTestId}-body`}>
                    <GroupBody
                        group={group}
                        visibleItems={displayed.visibleItems}
                        visibleSubgroups={displayed.visibleSubgroups}
                        accent={group.accent}
                        expandedSubgroups={expandedSubgroups}
                        onToggleSubgroup={onToggleSubgroup}
                    />
                </div>
            )}
        </div>
    );
}

/** Collapsed (icon-rail) mode. Each group is a 40-px tall icon
 *  button; hovering it reveals a flyout popover anchored to the
 *  right. The popover renders the same GroupBody as the expanded
 *  accordion — so the operator sees identical content regardless
 *  of which sidebar mode they're in. */
function GroupRail({ group, currentPath, user, can, expandedSubgroups, onToggleSubgroup }) {
    const Icon = group.icon;
    const visibleItems = (group.items || []).filter((i) => isItemVisible(i, user, can));
    const visibleSubgroups = (group.subgroups || [])
        .map((sg) => ({ ...sg, items: sg.items.filter((i) => isItemVisible(i, user, can)) }))
        .filter((sg) => sg.items.length > 0);
    if (visibleItems.length === 0 && visibleSubgroups.length === 0) return null;
    // UAT refinement: same display-time relabel/reshape used by the
    // expanded accordion (see resolveGroupDisplay above).
    const displayed = resolveGroupDisplay(group, visibleItems, visibleSubgroups, can);
    const groupOwnsCurrent = findGroupForPath(currentPath) === group.id;
    // PROP-001 / ADR-001 — accent fallback chain: group.accent → slate.
    const a = group.accent || "slate";
    return (
        <div
            className="relative group/rail"
            data-testid={`nav-rail-${group.id}`}
        >
            <button
                type="button"
                className={cn(
                    "h-10 w-10 mx-auto flex items-center justify-center rounded-md transition-colors",
                    groupOwnsCurrent
                        ? `bg-${a}-600 text-white`
                        : `text-${a}-600 hover:bg-${a}-50 hover:text-${a}-700`,
                )}
                title={displayed.label}
                aria-label={displayed.label}
            >
                <Icon size={18} weight={groupOwnsCurrent ? "fill" : "regular"} />
            </button>
            {/* Flyout popover — invisible until hover. The 4-px gap
                between the rail and the popover is bridged by an
                invisible padding so the cursor doesn't lose hover
                when crossing it. */}
            <div
                data-testid={`nav-rail-flyout-${group.id}`}
                className="absolute left-full top-0 z-40 pl-2 hidden group-hover/rail:block"
            >
                <div className="w-60 rounded-md border border-slate-200 bg-white shadow-lg py-2">
                    <div className="px-3 reedos-eyebrow uppercase tracking-[0.18em] text-[10px] text-slate-500 pb-1.5">
                        {displayed.label}
                    </div>
                    <div className="px-2">
                        <GroupBody
                            group={group}
                            visibleItems={displayed.visibleItems}
                            visibleSubgroups={displayed.visibleSubgroups}
                            accent={group.accent}
                            expandedSubgroups={expandedSubgroups}
                            onToggleSubgroup={onToggleSubgroup}
                            collapsed
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

/** Root-level Dashboard entry rendered above the group accordion.
 *  Stage 1 promotes Dashboard from a Sales group item to a Level-1
 *  standalone. Styled to look like a top-level group header (uppercase
 *  eyebrow) rather than a subordinate item so it reads as a peer to
 *  the group headers below it. */
function DashboardRootLink({ item }) {
    const Icon = item.icon;
    const location = useLocation();
    const active = location.pathname.startsWith(item.to);
    return (
        <div className="px-2 py-0.5" data-testid="nav-root-dashboard">
            <NavLink
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={cn(
                    "w-full flex items-center gap-2.5 pl-2.5 pr-3 py-2 rounded-md text-sm transition-colors border-l-[3px]",
                    active
                        ? "text-slate-900 font-bold border-indigo-600 bg-indigo-50"
                        : "text-slate-600 hover:text-slate-900 hover:bg-indigo-50/60 border-transparent",
                )}
            >
                <Icon
                    size={18}
                    weight={active ? "fill" : "regular"}
                    className={active ? "text-indigo-700" : "text-indigo-600"}
                />
                <span className="uppercase tracking-[0.16em] text-[12px] font-display font-semibold">
                    {item.label}
                </span>
            </NavLink>
        </div>
    );
}

/** Collapsed (icon-rail) variant of the Dashboard root entry. */
function DashboardRootRail({ item, currentPath }) {
    const Icon = item.icon;
    const active = currentPath.startsWith(item.to);
    return (
        <div className="relative" data-testid="nav-rail-root-dashboard">
            <NavLink
                to={item.to}
                title={item.label}
                aria-label={item.label}
                className={cn(
                    "h-10 w-10 mx-auto flex items-center justify-center rounded-md transition-colors",
                    active
                        ? "bg-indigo-600 text-white"
                        : "text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700",
                )}
            >
                <Icon size={18} weight={active ? "fill" : "regular"} />
            </NavLink>
        </div>
    );
}

// ── Main component ─────────────────────────────────────────────────
export const Sidebar = () => {
    const { user, can } = useAuth();
    const location = useLocation();

    // Sidebar-collapsed (icon rail) state. Stored globally because
    // it's a screen / chrome preference, not a per-operator one.
    const [collapsed, setCollapsed] = useState(() => {
        try {
            return window.localStorage.getItem(STORAGE_COLLAPSED_KEY) === "1";
        } catch {
            return false;
        }
    });
    useEffect(() => {
        try { window.localStorage.setItem(STORAGE_COLLAPSED_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
    }, [collapsed]);

    // Expanded-group state. Defaults to the group owning the current
    // path so the operator's destination is visible on first paint.
    // The choice is then persisted under a user-scoped key.
    const storageKey = STORAGE_EXPANDED_KEY(user?.email);
    const initialExpanded = useMemo(() => {
        let stored = null;
        try { stored = window.localStorage.getItem(storageKey); } catch { /* ignore */ }
        if (stored && NAV_GROUPS.some((g) => g.id === stored)) return stored;
        return findGroupForPath(location.pathname) || NAV_GROUPS[0].id;
    }, [storageKey]);
    const [expandedGroup, setExpandedGroup] = useState(initialExpanded);

    // ── Iter 101 · Stage 1.5 · L2 (subgroup) multi-open state ──
    // Independent from L1 — several subgroups may be open at once,
    // even across modules (though only the L1-open module renders
    // its subgroups at any given time). Persisted per-user as a
    // JSON array of subgroup ids under expandedSubgroups.{email}.
    const l2StorageKey = STORAGE_EXPANDED_L2_KEY(user?.email);
    const initialExpandedSubgroups = useMemo(() => {
        const set = new Set();
        try {
            const raw = window.localStorage.getItem(l2StorageKey);
            if (raw) {
                const arr = JSON.parse(raw);
                if (Array.isArray(arr)) {
                    for (const id of arr) {
                        if (typeof id === "string") set.add(id);
                    }
                }
            }
        } catch { /* ignore */ }
        // Deep-link seed: ensure the L2 owner of the current path is open.
        const { l2Id } = findOwnersForPath(location.pathname);
        if (l2Id) set.add(l2Id);
        return set;
    }, [l2StorageKey]);
    const [expandedSubgroups, setExpandedSubgroups] = useState(initialExpandedSubgroups);

    // Deep-link sync: when the user navigates (browser back, link
    // click, route change), auto-expand the owning L1 module AND
    // add the owning L2 subgroup to the open-set. We never
    // auto-COLLAPSE. Wrapped in Promise.resolve() so the setStates
    // run after the effect's commit phase (React 19 lint rule).
    useEffect(() => {
        const { l1Id, l2Id } = findOwnersForPath(location.pathname);
        if (!l1Id && !l2Id) return;
        Promise.resolve().then(() => {
            if (l1Id) setExpandedGroup((cur) => (cur !== l1Id ? l1Id : cur));
            if (l2Id) setExpandedSubgroups((cur) => {
                if (cur.has(l2Id)) return cur;
                const next = new Set(cur);
                next.add(l2Id);
                return next;
            });
        });
    }, [location.pathname]);

    // Persist expanded-group choice whenever it changes.
    useEffect(() => {
        try { window.localStorage.setItem(storageKey, expandedGroup); } catch { /* ignore */ }
    }, [expandedGroup, storageKey]);

    // Persist expanded-subgroups set whenever it changes.
    useEffect(() => {
        try {
            window.localStorage.setItem(l2StorageKey, JSON.stringify(Array.from(expandedSubgroups)));
        } catch { /* ignore */ }
    }, [expandedSubgroups, l2StorageKey]);

    const toggleGroup = (gid) => {
        // L1 accordion semantics: clicking the open group closes it;
        // clicking any other group opens that one (and implicitly
        // closes the previous — single-open).
        setExpandedGroup((cur) => (cur === gid ? null : gid));
    };

    const toggleSubgroup = (sgId) => {
        // L2 multi-open semantics: toggling one subgroup does not
        // affect others.
        setExpandedSubgroups((cur) => {
            const next = new Set(cur);
            if (next.has(sgId)) next.delete(sgId);
            else next.add(sgId);
            return next;
        });
    };

    return (
        <aside
            data-testid="app-sidebar"
            data-collapsed={collapsed ? "true" : "false"}
            className={cn(
                "hidden md:flex flex-col shrink-0 border-r border-slate-200 bg-white h-screen sticky top-0 transition-[width] duration-200",
                collapsed ? "w-16" : "w-64",
            )}
        >
            {/* Header ── pinned to the top */}
            <div className={cn(
                "flex items-center gap-2.5 h-16 border-b border-slate-200 shrink-0",
                collapsed ? "justify-center px-2" : "px-6",
            )}>
                <div className="h-7 w-7 bg-slate-900 rounded-md flex items-center justify-center shrink-0">
                    <span className="text-white font-display font-bold text-sm">R</span>
                </div>
                {!collapsed && (
                    <div className="leading-tight min-w-0">
                        <div className="font-display font-bold tracking-tight text-slate-900 text-base">REEDOS</div>
                        <div className="reedos-eyebrow text-[9px] tracking-[0.28em] mt-0.5">
                            Billing &amp; Commercial
                        </div>
                    </div>
                )}
            </div>

            {/* Scrollable nav region */}
            <div
                data-testid="sidebar-scroll"
                className="flex-1 min-h-0 overflow-y-auto reedos-sidebar-scroll scroll-smooth py-3"
            >
                {collapsed ? (
                    <div className="space-y-2">
                        {/* Dashboard root rail entry (Stage 1 — outside any group). */}
                        {isItemVisible(DASHBOARD_ITEM, user, can) && (
                            <DashboardRootRail
                                item={DASHBOARD_ITEM}
                                currentPath={location.pathname}
                            />
                        )}
                        {NAV_GROUPS.map((g) => (
                            <GroupRail
                                key={g.id}
                                group={g}
                                currentPath={location.pathname}
                                user={user}
                                can={can}
                                expandedSubgroups={expandedSubgroups}
                                onToggleSubgroup={toggleSubgroup}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="space-y-0.5">
                        {/* Dashboard root entry (Stage 1 — outside any group). */}
                        {isItemVisible(DASHBOARD_ITEM, user, can) && (
                            <DashboardRootLink item={DASHBOARD_ITEM} />
                        )}
                        {NAV_GROUPS.map((g) => (
                            <GroupAccordion
                                key={g.id}
                                group={g}
                                expanded={expandedGroup === g.id}
                                onToggle={() => toggleGroup(g.id)}
                                currentPath={location.pathname}
                                user={user}
                                can={can}
                                expandedSubgroups={expandedSubgroups}
                                onToggleSubgroup={toggleSubgroup}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Iter 102.3 · Phase B — Slim footer (EDDR-002).
                Profile / email / role badge / Sign-Out have moved to the
                top-right UserMenu in AppLayout. The sidebar footer now
                only carries the collapse/expand toggle + build stamp so
                the vertical real-estate is available for business
                navigation as the platform grows. */}
            <div className={cn(
                "border-t border-slate-200 shrink-0",
                collapsed ? "p-2" : "px-3 py-2",
            )}>
                {collapsed ? (
                    <div className="flex flex-col items-center gap-1.5">
                        <button
                            type="button"
                            onClick={() => setCollapsed(false)}
                            title="Expand sidebar"
                            data-testid="sidebar-expand-btn"
                            className="text-slate-400 hover:text-slate-900 p-1.5 rounded transition-colors"
                        >
                            <CaretRight size={14} weight="bold" />
                        </button>
                        <div className="text-[9px] text-slate-300 tracking-wide" title="Build v0.2.0">
                            v0.2.0
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-between px-1">
                        <button
                            type="button"
                            onClick={() => setCollapsed(true)}
                            title="Collapse sidebar (icon-only mode)"
                            data-testid="sidebar-collapse-btn"
                            className="text-[11px] text-slate-400 hover:text-slate-800 inline-flex items-center gap-1.5 transition-colors"
                        >
                            <CaretLeft size={11} weight="bold" />
                            <SidebarIcon size={12} />
                            Collapse
                        </button>
                        <div className="text-[10px] text-slate-400 tracking-wide">
                            Standalone &middot; v0.2.0
                        </div>
                    </div>
                )}
            </div>
        </aside>
    );
};

export default Sidebar;
